import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";

interface CampaignMove {
  fromIndex: number;
  toIndex: number;
}

// Curated block = the campaigns already carrying a manual order; they always sort ahead of the rest.
function countCuratedCampaigns(campaigns: InfluencerCampaign[]): number {
  return campaigns.filter((campaign) => campaign.sortOrder !== undefined).length;
}

/**
 * Campaign IDs to persist as the manual order after one card moves.
 *
 * Only the curated block is returned, never the whole column: campaigns left out keep no manual
 * order and stay on creation-date ordering, so newly created campaigns still surface near the top
 * instead of sinking below every campaign that happened to be on screen when someone reordered.
 * The block spans far enough to cover both ends of the move plus any existing curation.
 */
export function moveCampaignInOrder(
  campaigns: InfluencerCampaign[],
  { fromIndex, toIndex }: CampaignMove,
): string[] {
  const orderedCampaignIds = campaigns.map((campaign) => campaign.campaignId);
  // A move that goes nowhere must not curate anything: doing so would silently pin the campaign
  // and everything above it without the admin having reordered.
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= orderedCampaignIds.length ||
    toIndex >= orderedCampaignIds.length
  ) {
    return orderedCampaignIds.slice(0, countCuratedCampaigns(campaigns));
  }
  const [movedCampaignId] = orderedCampaignIds.splice(fromIndex, 1);
  if (movedCampaignId === undefined) return orderedCampaignIds;
  orderedCampaignIds.splice(toIndex, 0, movedCampaignId);
  const curatedEndIndex = Math.max(
    countCuratedCampaigns(campaigns) - 1,
    fromIndex,
    toIndex,
  );
  return orderedCampaignIds.slice(0, curatedEndIndex + 1);
}

/**
 * The campaign list as the server will return it once the given order is saved, so the board can
 * render the new order immediately instead of waiting for the refetch.
 *
 * Mirrors the facade ordering exactly — curated block first, everyone else by newest first — and
 * rewrites every `sortOrder`, because a follow-up move reads them to size the curated block.
 */
export function applyCampaignOrder(
  campaigns: InfluencerCampaign[],
  orderedCampaignIds: string[],
): InfluencerCampaign[] {
  const positionById = new Map(
    orderedCampaignIds.map((campaignId, position) => [campaignId, position]),
  );
  const curated = campaigns
    .filter((campaign) => positionById.has(campaign.campaignId))
    .map((campaign) => ({
      ...campaign,
      sortOrder: positionById.get(campaign.campaignId),
    }))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const uncurated = campaigns
    .filter((campaign) => !positionById.has(campaign.campaignId))
    .map(({ sortOrder: _sortOrder, ...campaign }) => campaign)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return [...curated, ...uncurated];
}
